<script setup lang="ts">
import { computed, ref } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import type { Teach } from '@pocketprep/types'
import EditStudentSidePanel from '@/components/SidePanels/EditStudent.vue'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { licenseModule } from '@/store/license/module'
import { studentModule } from '@/store/student/module'

const {
    Button: PocketButton,
} = UIKit

const route = useRoute()

const showEditStudentSidePanel = ref(false)

const currentPendingStudentId = computed(() => {
    return route.params.studentId as string
})

const currentGroupExam = computed(() => {
    return examModule.getters.getCurrentGroupExam()
})

const currentOrgPendingLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses().filter(l => l.status === 'pending')
})

const pendingStudentEmail = computed(() => {
    const pendingStudent = currentOrgPendingLicenses.value.find(opl => {
        return opl.orgStudent?.objectId === currentPendingStudentId.value
    })

    return pendingStudent && pendingStudent.userEmail
})

const isStatsPage = computed(() => {
    const routeName = route.name as string | undefined
    return !!routeName && routeName.includes('stats')
})

const examName = (exam: Teach.Class.OrgExamJSON) => {
    return exam.displayName || ('nativeAppName' in exam.examMetadata && exam.examMetadata.nativeAppName)
}

const closeEditStudentSidePanel = () => {
    showEditStudentSidePanel.value = false
}

const openEditStudentSidePanel = () => {
    showEditStudentSidePanel.value = true
}

onBeforeRouteUpdate((to, from, next) => {
    groupModule.actions.updateCurrentGroupId(to.params.groupId as string)
    studentModule.actions.updateCurrentStudentId(to.params.studentId as string)
    next()
})
</script>

<template>
    <div>
        <EditStudentSidePanel
            v-if="showEditStudentSidePanel"
            :org-student-id="currentPendingStudentId"
            @close="closeEditStudentSidePanel"
        />
        <div class="pending-student-stats-head">
            <div v-if="currentGroupExam" class="pending-student-stats-head__exams">
                <div
                    class="pending-student-stats-head__exams-label"
                >
                    {{ examName(currentGroupExam) }}
                </div>
            </div>
            <PocketButton
                v-if="isStatsPage"
                class="pending-student-stats-head__edit-student-btn"
                type="tertiary-small"
                @click="openEditStudentSidePanel"
            >
                Edit Pending Student
            </PocketButton>
        </div>
        <div class="pending-student-stats-head__stats">
            <div class="pending-student-stats-head__stats-email">
                <div>
                    {{ pendingStudentEmail }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.pending-student-stats-head {
    display: flex;
    font-size: 14px;
    justify-content: space-between;
    margin-bottom: 24px;

    &__exams {
        line-height: 19px;
        position: relative;
        color: $slate-01;
    }

    &__exams-label {
        user-select: none;
        outline: none;
    }

    &__edit-student-btn {
        padding: 0 !important;
    }

    &__stats-email {
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        overflow-wrap: break-word;
    }
}
</style>
